import type {
  OfferPageFilterType,
  OfferPagePriceMode,
  OfferPageQuickFilters,
  OfferPageRoom,
  OfferPageState,
} from 'contexts/OfferPage/offerPageStateReducer'
import type { RoomRateViews } from 'components/OfferPage/Property/singlePropertyUtils'
import { onlyDefinedProperties } from 'lib/object/objectUtils'
import type {
  SurfaceAtomRequest,
  SurfaceRecentCommittedStateRequest,
} from 'tripPlanner/assistant/api/surfaceSnapshotContract'
import { normalizeAssistantRoomOccupancy } from 'tripPlanner/assistant/utils/assistantRoomOccupancyText'
import { nonEmptyString } from './offerPageContextFormatting'

export interface OfferPageSnapshotRoom {
  id: string
  adults: number
  children?: number
  teenagers?: number
  infants?: number
  childrenAge?: Array<number>
  roomId?: string
  rateId?: string
}

export interface OfferPageSnapshotSelection {
  activeFilter?: OfferPageFilterType
  activeSubFilter?: OfferPageFilterType
  activeHeaderFilter?: OfferPageFilterType
  airportCode?: string
  checkInDate?: string
  checkOutDate?: string
  duration: number
  durationSelected?: boolean
  flights: boolean
  isMultiRoomConnected?: boolean
  multiRoomMode: OfferPageState['multiRoomMode']
  isMultiRoomSelectRooms?: boolean
  displayMemberPrice?: boolean
  selectedBedbankRoomId?: string
  selectedPackageKey?: string
  propertyRoomId?: string
  roomOption?: {
    roomRateId: string
    roomTypeId: string
  }
  quickFilters?: Partial<OfferPageQuickFilters>
  isAlternativeDatesFlow?: boolean
  charterBundleEnabled: boolean
  fareClass: OfferPageState['fareClass']
  priceMode: OfferPagePriceMode
  pointsToApply?: number
  pointsHideZeroCash?: boolean
  rooms: Array<OfferPageSnapshotRoom>
}

export interface OfferPageSnapshotPriceFormatContext {
  currencyCode: string
  regionCode: string
}

interface OfferPageSnapshotSourceBase {
  key: string
  selection: OfferPageSnapshotSelection
  priceFormatContext: OfferPageSnapshotPriceFormatContext
  pageUrl?: string
}

export interface FlashOfferPageSnapshotSource extends OfferPageSnapshotSourceBase {
  variant: 'flash'
  offer: App.HotelOffer
  packageViews: Array<App.OfferPackageOptionView>
}

export interface LmePropertyOfferPageSnapshotSource extends OfferPageSnapshotSourceBase {
  variant: 'lme_property'
  property: App.Property.Property
  roomRateViews: Array<RoomRateViews>
  ratesStatus?: 'loading' | 'unavailable'
}

export interface BedbankOfferPageSnapshotSource extends OfferPageSnapshotSourceBase {
  variant: 'bedbank'
  offer: App.BedbankOffer
  packageViews: Array<App.BedbankOfferPackageView>
}

export interface SppPropertyOfferPageSnapshotSource extends OfferPageSnapshotSourceBase {
  variant: 'spp_property'
  property: App.Property.Property
  roomRateViews: Array<RoomRateViews>
  ratesStatus?: 'loading' | 'unavailable'
}

export type OfferPageAssistantSource =
  | FlashOfferPageSnapshotSource
  | LmePropertyOfferPageSnapshotSource
  | BedbankOfferPageSnapshotSource
  | SppPropertyOfferPageSnapshotSource

export interface OfferPageSnapshotContribution {
  key: string
  sourcePath: string
  visibleState?: {
    activeControl?: string
    activeDrawer?: string
    activeModal?: string
    suppressAutoFocus: boolean
  }
  recentCommittedState: Array<SurfaceRecentCommittedStateRequest>
  atoms: Array<SurfaceAtomRequest>
}

export interface OfferPageSnapshotInput {
  source: OfferPageAssistantSource
  contributions: Array<OfferPageSnapshotContribution>
}

export function withOfferPageSnapshotPageUrl(
  input: OfferPageSnapshotInput,
  pageUrl: string,
): OfferPageSnapshotInput {
  return { ...input, source: { ...input.source, pageUrl } }
}

function copyRoom(room: OfferPageRoom): OfferPageSnapshotRoom {
  // Canonicalise sparse assistant storage; age categorisation runs at format time with offer thresholds.
  const occupancy = normalizeAssistantRoomOccupancy(room)

  return onlyDefinedProperties({
    id: room.id,
    adults: occupancy.adults,
    children: occupancy.children,
    teenagers: occupancy.teenagers,
    infants: occupancy.infants,
    childrenAge: occupancy.childrenAge?.length ? [...occupancy.childrenAge] : undefined,
    roomId: room.roomId,
    rateId: room.rateId,
  })
}

export function createOfferPageSnapshotSelection(
  state: OfferPageState,
): OfferPageSnapshotSelection {
  return onlyDefinedProperties({
    activeFilter: state.activeFilter,
    activeSubFilter: state.activeSubFilter,
    activeHeaderFilter: state.activeHeaderFilter,
    airportCode: nonEmptyString(state.airport?.code),
    checkInDate: state.checkInDate,
    checkOutDate: state.checkOutDate,
    duration: state.duration,
    durationSelected: state.durationSelected,
    flights: state.flights,
    isMultiRoomConnected: state.isMultiRoomConnected,
    multiRoomMode: state.multiRoomMode,
    isMultiRoomSelectRooms: state.isMultiRoomSelectRooms,
    displayMemberPrice: state.displayMemberPrice,
    selectedBedbankRoomId: state.selectedBedbankRoomId,
    selectedPackageKey: state.selectedPackageKey,
    propertyRoomId: state.propertyRoomId,
    roomOption: state.roomOption ? {
      roomRateId: state.roomOption.roomRateId,
      roomTypeId: state.roomOption.roomTypeId,
    } : undefined,
    quickFilters: state.quickFilters !== undefined ? { ...state.quickFilters } : undefined,
    isAlternativeDatesFlow: state.isAlternativeDatesFlow,
    charterBundleEnabled: state.charterBundleEnabled,
    fareClass: state.fareClass,
    priceMode: state.priceMode,
    pointsToApply: state.pointsToApply,
    pointsHideZeroCash: state.pointsHideZeroCash,
    rooms: state.rooms.map(copyRoom),
  })
}
